import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Save, NotebookPen, FileText, Paperclip, Users, X, ListChecks } from "lucide-react";
import AttachPDPicker from "../components/AttachPDPicker";
import CalibrationTracker from "../components/CalibrationTracker";

const PERIODS = [
  { v: "1m", label: "Past month" },
  { v: "3m", label: "Past 3 months" },
  { v: "6m", label: "Past 6 months" },
  { v: "12m", label: "Past 12 months" },
];

function status(c) {
  if (c.employee_submitted && c.manager_submitted && c.conversation_held_on) return { t: "CONVERSATION HELD", cls: "cal-badge-unlocked" };
  if (c.employee_submitted && c.manager_submitted) return { t: "UNLOCKED", cls: "cal-badge-unlocked" };
  if (c.employee_submitted) return { t: "WAITING ON MANAGER", cls: "cal-badge-waiting" };
  if (c.manager_submitted) return { t: "WAITING ON EMPLOYEE", cls: "cal-badge-waiting" };
  return { t: "IN PROGRESS", cls: "cal-badge-waiting" };
}

export default function ProfileDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [cals, setCals] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showStart, setShowStart] = useState(false);
  const [startForm, setStartForm] = useState({ title: "", period: "3m" });
  const [starting, setStarting] = useState(false);
  const [notes, setNotes] = useState([]);
  const [notesSaving, setNotesSaving] = useState(false);
  const [newNoteText, setNewNoteText] = useState("");
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingText, setEditingText] = useState("");
  // Floating editor drawers (Iter40 — cleaned up ProfileDetail layout)
  const [showTasksDrawer, setShowTasksDrawer] = useState(false);
  const [showNotesDrawer, setShowNotesDrawer] = useState(false);
  // Inline department editor — a small pill next to the title/email that
  // pops into an input on click. Keeps the profile header quiet when
  // department is already set.
  const [editingDept, setEditingDept] = useState(false);
  const [deptDraft, setDeptDraft] = useState("");
  const [deptSaving, setDeptSaving] = useState(false);
  const [orgDepartments, setOrgDepartments] = useState([]);
  // 360° Review Cycles (Phase 1) — only surface if plan has it enabled.
  const [cycles, setCycles] = useState([]);
  const [plan360Enabled, setPlan360Enabled] = useState(false);
  const [show360Modal, setShow360Modal] = useState(false);
  const [cycle360Form, setCycle360Form] = useState({ title: "", period: "6m" });
  const [starting360, setStarting360] = useState(false);
  useEffect(() => {
    api.get("/profiles/departments")
      .then((r) => setOrgDepartments(Array.isArray(r.data) ? r.data : []))
      .catch(() => setOrgDepartments([]));
    // Check plan flag once per mount — this gates the 360 UI entirely.
    api.get("/org/me")
      .then((r) => setPlan360Enabled(!!r.data?.plan?.review_360_enabled))
      .catch(() => setPlan360Enabled(false));
  }, []);

  // Backend serves notes as `[{id, text, created_at, updated_at}, …]` since
  // Iter41. Legacy string-only profiles are normalised server-side to a
  // single `{id: "legacy-blob", …}` entry so we never see a raw string here.
  const load = () => api.get(`/profiles/${id}`).then((r) => {
    setProfile(r.data.profile);
    setCals(r.data.calibrations);
    setTasks(r.data.profile.tasks || []);
    const rawNotes = Array.isArray(r.data.profile.notes) ? r.data.profile.notes : [];
    // Sort newest-first for display
    setNotes([...rawNotes].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")));
    setDirty(false);
  }).catch((e) => toast.error(apiError(e, "Not found")));

  // Load any 360 cycles associated with this profile. Scoped to the signed-in
  // manager by the backend; failures (e.g. plan disabled) just leave the list
  // empty — the section will be hidden by `plan360Enabled` anyway.
  const loadCycles = () => api.get(`/review-cycles`, { params: { profile_id: id } })
    .then((r) => setCycles(r.data?.cycles || []))
    .catch(() => setCycles([]));

  useEffect(() => { load(); loadCycles(); }, [id]);

  // Close drawers on Escape (nice UX — matches the Shadcn/UI dialog pattern).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { setShowTasksDrawer(false); setShowNotesDrawer(false); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const updTask = (idx, val) => { setTasks((t) => t.map((x, i) => i === idx ? { ...x, description: val } : x)); setDirty(true); };
  const rmTask = (idx) => { setTasks((t) => t.filter((_, i) => i !== idx)); setDirty(true); };
  const addTask = () => { setTasks((t) => [...t, { id: "", description: "" }]); setDirty(true); };

  const saveTasks = async () => {
    const clean = tasks.map((t) => ({ id: t.id || "", description: t.description.trim() })).filter((t) => t.description);
    if (!clean.length) { toast.error("Need at least one task"); return; }
    setSaving(true);
    try {
      const r = await api.put(`/profiles/${id}/tasks`, { tasks: clean });
      setTasks(r.data.tasks);
      setDirty(false);
      toast.success("Tasks saved");
    } catch (err) { toast.error(apiError(err, "Save failed")); }
    finally { setSaving(false); }
  };

  // Date-stamped notes (Iter41) — timeline of individual entries, each
  // created / edited / deleted via its own REST call. Backend returns the
  // full newest-first list on every mutation.
  const addNote = async () => {
    const text = newNoteText.trim();
    if (!text) return;
    setNotesSaving(true);
    try {
      const r = await api.post(`/profiles/${id}/notes`, { text });
      setNotes(r.data.notes);
      setNewNoteText("");
      toast.success("Note added");
    } catch (err) { toast.error(apiError(err, "Could not add note")); }
    finally { setNotesSaving(false); }
  };

  const saveNoteEdit = async (noteId) => {
    const text = editingText.trim();
    if (!text) { setEditingNoteId(null); return; }
    setNotesSaving(true);
    try {
      const r = await api.patch(`/profiles/${id}/notes/${noteId}`, { text });
      setNotes(r.data.notes);
      setEditingNoteId(null);
      setEditingText("");
    } catch (err) { toast.error(apiError(err, "Could not save edit")); }
    finally { setNotesSaving(false); }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm("Delete this note? This can't be undone.")) return;
    setNotesSaving(true);
    try {
      const r = await api.delete(`/profiles/${id}/notes/${noteId}`);
      setNotes(r.data.notes);
      toast.success("Note deleted");
    } catch (err) { toast.error(apiError(err, "Delete failed")); }
    finally { setNotesSaving(false); }
  };

  const saveDepartment = async () => {
    const value = deptDraft.trim();
    setDeptSaving(true);
    try {
      const r = await api.patch(`/profiles/${id}`, { department: value });
      setProfile(r.data);
      setEditingDept(false);
      if (value && !orgDepartments.some((d) => d.toLowerCase() === value.toLowerCase())) {
        setOrgDepartments((prev) => [...prev, value].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())));
      }
      toast.success("Department updated");
    } catch (err) {
      toast.error(apiError(err, "Failed to update department"));
    } finally {
      setDeptSaving(false);
    }
  };

  const startCalibration = async () => {
    if (!startForm.title.trim()) { toast.error("Title required"); return; }
    setStarting(true);
    try {
      const r = await api.post(`/profiles/${id}/calibrations`, startForm);
      toast.success("Calibration created");
      nav(`/calibrations/${r.data.calibration_id}`);
    } catch (err) { toast.error(apiError(err, "Failed")); setStarting(false); }
  };

  const start360Cycle = async () => {
    setStarting360(true);
    try {
      const r = await api.post(`/review-cycles`, {
        profile_id: id,
        period: cycle360Form.period,
        title: cycle360Form.title.trim() || undefined,
      });
      toast.success(`360° review started — ${profile.employee_name} will receive a nomination link.`);
      setShow360Modal(false);
      setCycle360Form({ title: "", period: "6m" });
      await loadCycles();
      // Also surface the nomination URL so the manager can share it directly
      // if the employee misses the email.
      if (r.data?.nomination_url) {
        toast.message("Nomination link ready — also copied to the cycle details.");
      }
    } catch (err) {
      toast.error(apiError(err, "Could not start 360 review"));
    } finally {
      setStarting360(false);
    }
  };

  const cancel360Cycle = async (cid, title) => {
    if (!window.confirm(`Cancel "${title}"? This stops the cycle before any raters are invited.`)) return;
    try {
      await api.post(`/review-cycles/${cid}/cancel`);
      toast.success("Cycle cancelled");
      await loadCycles();
    } catch (err) {
      toast.error(apiError(err, "Could not cancel"));
    }
  };

  if (!profile) return <div className="p-10 font-mono-cal text-xs">[LOADING…]</div>;

  // Client-side mirror of the server-side guard in POST /profiles/:id/calibrations.
  // Disabling the button matches the 409 the API would otherwise throw, and
  // gives the manager a useful hint about which calibration is blocking.
  const openCal = cals.find((c) => !(c.employee_submitted && c.manager_submitted));
  const trackerCal = openCal || cals.find((c) => c.employee_submitted && c.manager_submitted && !c.conversation_held_on);
  // Same guard for 360 cycles — only one open cycle per profile at a time.
  const openCycle = cycles.find((c) => ["nominating","awaiting_approval","collecting","locked_pending"].includes(c.status));

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-12">
      <Link to="/dashboard" className="font-mono-cal text-xs text-[#666] hover:text-[#FF4F00]" data-testid="back-dashboard">← DASHBOARD</Link>

      <div className="mt-6 flex flex-wrap items-end justify-between gap-4 mb-2">
        <div>
          <div className="font-mono-cal text-[11px] uppercase tracking-[0.2em] text-[#666]">[PROFILE]</div>
          <h1 className="font-display font-bold text-5xl tracking-tight mt-2">{profile.employee_name}</h1>
          <div className="text-[#666] mt-1">
            {profile.employee_title || "—"} · <span className="font-mono-cal text-sm">{profile.employee_email}</span>
          </div>
          <div className="mt-2" data-testid="department-line">
            {editingDept ? (
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  list="pd-detail-departments"
                  autoFocus
                  className="cal-input !py-1 !px-2 max-w-[240px]"
                  value={deptDraft}
                  onChange={(e) => setDeptDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); saveDepartment(); }
                    if (e.key === "Escape") { setEditingDept(false); }
                  }}
                  placeholder="Engineering, Sales, Ops…"
                  maxLength={140}
                  data-testid="department-input"
                />
                <datalist id="pd-detail-departments">
                  {orgDepartments.map((d) => <option key={d} value={d} />)}
                </datalist>
                <button
                  onClick={saveDepartment}
                  disabled={deptSaving}
                  className="cal-btn !py-1 !px-2 text-xs"
                  data-testid="department-save"
                >{deptSaving ? "Saving…" : "Save"}</button>
                <button
                  onClick={() => setEditingDept(false)}
                  disabled={deptSaving}
                  className="cal-btn cal-btn-ghost !py-1 !px-2 text-xs"
                  data-testid="department-cancel"
                >Cancel</button>
              </div>
            ) : profile.department ? (
              <button
                type="button"
                onClick={() => { setDeptDraft(profile.department || ""); setEditingDept(true); }}
                className="inline-flex items-center gap-1 px-2 py-1 border border-[#111] text-xs font-mono-cal uppercase tracking-widest hover:bg-[#F4F4F0]"
                data-testid="department-pill"
              >
                <span className="text-[#666]">DEPT ·</span> <span className="font-display font-semibold normal-case tracking-normal text-[#111]">{profile.department}</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => { setDeptDraft(""); setEditingDept(true); }}
                className="text-xs font-mono-cal text-[#FF4F00] hover:underline"
                data-testid="department-add-btn"
              >+ Add department</button>
            )}
          </div>
          <div className="mt-3 font-mono-cal text-[11px] text-[#B00020]" data-testid="pd-filename-line">
            {profile.pd_filename ? (
              <span className="text-[#666]">PD · {profile.pd_filename}</span>
            ) : (
              <>No PD attached — tasks not yet generated</>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              onClick={() => setShowTasksDrawer(true)}
              className="cal-btn cal-btn-ghost flex items-center gap-1"
              data-testid="open-tasks-drawer"
              title={`Edit tasks (${tasks.length})`}
            >
              <ListChecks size={14} /> Tasks
              <span className="ml-1 font-mono-cal text-[10px] bg-[#111] text-white px-1.5 py-0.5" data-testid="tasks-count-badge">
                {tasks.length}
              </span>
              {dirty && <span className="ml-1 w-2 h-2 bg-[#FF4F00] rounded-full" data-testid="tasks-dirty-dot" title="Unsaved changes" />}
            </button>
            <button
              onClick={() => setShowNotesDrawer(true)}
              className="cal-btn cal-btn-ghost flex items-center gap-1"
              data-testid="open-notes-drawer"
              title="Open private manager notes"
            >
              <NotebookPen size={14} /> Notes
              {notes.length > 0 && (
                <span className="ml-1 font-mono-cal text-[10px] bg-[#111] text-white px-1.5 py-0.5" data-testid="notes-count-badge">
                  {notes.length}
                </span>
              )}
            </button>
            <Link to="/pd-generator" className="cal-btn cal-btn-ghost" data-testid="pd-generator-btn">
              <FileText size={14} /> PD Generator
            </Link>
            <button
              onClick={() => setShowStart(true)}
              disabled={!!openCal}
              className="cal-btn disabled:opacity-50 disabled:cursor-not-allowed"
              title={openCal ? `Blocked — "${openCal.title}" is still open` : ""}
              data-testid="start-calibration-btn"
            >+ Start calibration</button>
            {plan360Enabled && (
              <button
                onClick={() => setShow360Modal(true)}
                disabled={!!openCycle || !(tasks?.length)}
                className="cal-btn disabled:opacity-50 disabled:cursor-not-allowed"
                title={
                  openCycle ? `Blocked — "${openCycle.title}" is still in progress`
                  : !tasks?.length ? "Add tasks before starting a 360"
                  : "Launch a 360° review for this employee"
                }
                data-testid="start-360-btn"
              >
                <Users size={14} /> Start 360° review
              </button>
            )}
          </div>
          {openCal && (
            <span className="font-mono-cal text-[10px] uppercase tracking-widest text-[#B00020] max-w-[280px] text-right" data-testid="start-calibration-blocked">
              Complete or delete “{openCal.title}” first
            </span>
          )}
        </div>
      </div>

      {/* Next-step guidance */}
      <div className="mt-8" data-testid="profile-next-step">
        {trackerCal ? (
          <CalibrationTracker cal={{ ...trackerCal, employee_name: profile.employee_name }} title={trackerCal.title} />
        ) : (
          <div className="cal-card p-5 md:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="profile-next-step-card">
            <div>
              <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00]">[ NEXT STEP ]</div>
              <p className="text-sm mt-1" data-testid="profile-next-step-text">
                {tasks.length === 0
                  ? `Add ${profile.employee_name.split(" ")[0]}'s Position Description (or add tasks manually) so there's something to rate.`
                  : cals.length === 0
                  ? `Tasks are ready. Start a calibration — ${profile.employee_name.split(" ")[0]} gets a secret link to add goals and self-rate.`
                  : "All calibrations are complete and discussed. Start a new one whenever the next review period comes around."}
              </p>
            </div>
            {tasks.length === 0 ? (
              <button onClick={() => setShowTasksDrawer(true)} className="cal-btn whitespace-nowrap" data-testid="profile-next-step-cta"><ListChecks size={14} /> Add PD / tasks</button>
            ) : (
              <button onClick={() => setShowStart(true)} className="cal-btn whitespace-nowrap" data-testid="profile-next-step-cta">+ Start calibration</button>
            )}
          </div>
        )}
      </div>

      {/* Calibrations — now full-width since Tasks + Notes moved to drawers (Iter40) */}
      <div className="mt-10">
        <h2 className="font-display font-bold text-3xl mb-4">Calibrations</h2>
        {cals.length === 0 ? (
          <div className="cal-card p-6 text-center" data-testid="cals-empty">
            <div className="font-mono-cal text-xs text-[#666] mb-2">[ NONE YET ]</div>
            <div className="text-sm text-[#666]">Start your first calibration to begin assessing.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="cals-list">
            {cals.map((c) => {
              const s = status(c);
              return (
                <button key={c.id} onClick={() => nav(`/calibrations/${c.id}`)}
                  className="cal-card p-4 text-left hover:bg-[#F4F4F0] transition"
                  data-testid={`cal-${c.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-display font-bold text-base truncate">{c.title}</div>
                    <span className={`cal-badge ${s.cls} text-[9px]`}>{s.t}</span>
                  </div>
                  <div className="mt-1 text-xs text-[#666] font-mono-cal">{c.period_label} · {c.task_count}T + {c.goal_count}G</div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* 360° Review Cycles — Phase 1: list + launcher. Only visible when
          the plan has `review_360_enabled`. Rater nomination (Phase 2) and
          magic-link submissions (Phase 3) land in follow-up iterations. */}
      {plan360Enabled && (
        <div className="mt-10" data-testid="review-cycles-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00] flex items-center gap-2">
                <Users size={12} /> [ 360° REVIEWS ]
              </div>
              <h2 className="font-display font-bold text-3xl mt-1">Multi-rater reviews</h2>
              <p className="text-[#666] text-sm">Nominate peers, direct reports and cross-functional partners. Unlocks when the vault threshold is met.</p>
            </div>
          </div>
          {cycles.length === 0 ? (
            <div className="cal-card p-6 text-center" data-testid="cycles-empty">
              <div className="font-mono-cal text-xs text-[#666] mb-2">[ NO 360 CYCLES YET ]</div>
              <div className="text-sm text-[#666]">Click <strong>Start 360° review</strong> above to launch one. {profile.employee_name.split(" ")[0]} will receive a nomination link via email.</div>
            </div>
          ) : (
            <div className="cal-card overflow-hidden" data-testid="cycles-list">
              {cycles.map((c, i) => (
                <div key={c.id} className={`p-4 ${i > 0 ? "border-t border-[#111]" : ""}`} data-testid={`cycle-${c.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="font-display font-bold text-base truncate">{c.title}</div>
                    <span className={`cal-badge text-[9px] ${
                      c.status === "nominating" ? "cal-badge-waiting"
                      : c.status === "awaiting_approval" ? "cal-badge-waiting"
                      : c.status === "collecting" ? "cal-badge-waiting"
                      : c.status === "locked_pending" ? "cal-badge-waiting"
                      : c.status === "unlocked" ? "cal-badge-unlocked"
                      : "" }`}>{c.status.replace("_", " ").toUpperCase()}</span>
                  </div>
                  <div className="mt-1 text-xs text-[#666] font-mono-cal">
                    {c.period_label} · min {c.min_peers} peers · {c.max_total_raters} raters max
                    · weights M {Math.round(c.weights.manager*100)}% / P {Math.round(c.weights.peers*100)}% / DR {Math.round(c.weights.direct_reports*100)}%
                  </div>
                  {c.status === "nominating" && (
                    <div className="mt-3 flex gap-2 flex-wrap items-center">
                      <span className="font-mono-cal text-[10px] text-[#666] uppercase">[ WAITING ON EMPLOYEE NOMINATIONS ]</span>
                      <button
                        onClick={async () => {
                          try {
                            const r = await api.get(`/review-cycles/${c.id}`);
                            const path = r.data?.nomination_url || "";
                            const full = path.startsWith("http") ? path : `${window.location.origin}${path}`;
                            await navigator.clipboard.writeText(full);
                            toast.success("Nomination link copied to clipboard");
                          } catch {
                            toast.error("Could not copy link");
                          }
                        }}
                        className="font-mono-cal text-[10px] uppercase tracking-widest text-[#111] underline"
                        data-testid={`cycle-copy-link-${c.id}`}
                      >Copy nomination link</button>
                      <button
                        onClick={() => cancel360Cycle(c.id, c.title)}
                        className="font-mono-cal text-[10px] uppercase tracking-widest text-[#B00020] underline"
                        data-testid={`cycle-cancel-${c.id}`}
                      >Cancel cycle</button>
                    </div>
                  )}
                  {c.status === "awaiting_approval" && (
                    <div className="mt-3 flex gap-2 flex-wrap items-center">
                      <span className="font-mono-cal text-[10px] text-[#FF4F00] uppercase">[ {(c.nominated_raters || []).length} RATERS PROPOSED — YOUR APPROVAL NEEDED ]</span>
                      <button
                        onClick={() => nav(`/360/approve/${c.id}`)}
                        className="cal-btn text-xs py-1"
                        data-testid={`cycle-approve-${c.id}`}
                      >Review nominations →</button>
                    </div>
                  )}
                  {c.status === "collecting" && (
                    <div className="mt-3 space-y-2">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-mono-cal text-[10px] text-[#00A36C] uppercase">
                          [ COLLECTING · {c.progress?.raters_responded ?? 0} / {c.progress?.raters_invited ?? (c.approved_raters || []).length} RATERS RESPONDED ]
                        </span>
                      </div>
                      <div className="flex gap-3 flex-wrap text-[11px] font-mono-cal uppercase tracking-widest" data-testid={`cycle-gates-${c.id}`}>
                        <span className={((c.progress?.submissions_by_role?.peer || 0) >= c.min_peers) ? "text-[#00A36C]" : "text-[#B00020]"}>
                          ● peers {c.progress?.submissions_by_role?.peer || 0}/{c.min_peers}
                        </span>
                        <span className={c.progress?.manager_submitted ? "text-[#00A36C]" : "text-[#B00020]"}>
                          ● manager {c.progress?.manager_submitted ? "✓" : "pending"}
                        </span>
                        <span className={c.progress?.self_submitted ? "text-[#00A36C]" : "text-[#B00020]"}>
                          ● self {c.progress?.self_submitted ? "✓" : "pending"}
                        </span>
                      </div>
                      {(c.approved_raters || []).length > 0 && (
                        <details className="text-xs text-[#333]">
                          <summary className="cursor-pointer font-mono-cal text-[10px] text-[#666] uppercase tracking-widest hover:text-[#111]">
                            View rater progress
                          </summary>
                          <div className="mt-2 border border-[#111]/10 divide-y divide-[#111]/10" data-testid={`cycle-raters-${c.id}`}>
                            {(c.approved_raters || []).map((r) => {
                              const submitted = (c.progress?.submissions_by_role?.[r.role_type] ?? 0) > 0;
                              return (
                                <div key={r.id} className="p-2 flex items-center justify-between gap-2 flex-wrap" data-testid={`cycle-rater-row-${r.id}`}>
                                  <div>
                                    <span className="font-display font-semibold">{r.name}</span>
                                    <span className="ml-2 font-mono-cal text-[9px] uppercase text-[#666]">[{r.role_type.replace("_", " ")}]</span>
                                    <span className="ml-2 text-[11px] text-[#666]">{r.email}</span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      try {
                                        await api.post(`/review-cycles/${c.id}/resend-invite/${r.id}`);
                                        toast.success(`Reminder sent to ${r.name}`);
                                      } catch (err) {
                                        toast.error(apiError(err, "Could not resend"));
                                      }
                                    }}
                                    className="font-mono-cal text-[10px] uppercase tracking-widest text-[#111] underline"
                                    data-testid={`cycle-rater-resend-${r.id}`}
                                  >Resend invite</button>
                                </div>
                              );
                            })}
                          </div>
                        </details>
                      )}
                      <div className="text-xs text-[#666]">
                        Submissions are sealed in the vault — nobody (including you) can view individual scores until all three gates close.
                      </div>
                    </div>
                  )}
                  {c.status === "unlocked" && (
                    <div className="mt-3 flex items-center gap-3 flex-wrap">
                      <span className="font-mono-cal text-[10px] text-[#00A36C] uppercase">[ VAULT UNLOCKED · AGGREGATED RESULTS READY ]</span>
                      <button
                        onClick={() => nav(`/360/results/${c.id}`)}
                        className="cal-btn text-xs py-1"
                        data-testid={`cycle-view-results-${c.id}`}
                      >View results →</button>
                    </div>
                  )}
                  {c.status === "locked_pending" && (
                    <div className="mt-3 flex items-center gap-3 flex-wrap">
                      <button
                        onClick={() => nav(`/360/results/${c.id}`)}
                        className="font-mono-cal text-[10px] uppercase tracking-widest text-[#111] underline"
                        data-testid={`cycle-view-progress-${c.id}`}
                      >View vault progress →</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Start 360 modal */}
      {show360Modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="start-360-modal">
          <div className="cal-card cal-shadow max-w-md w-full p-8 bg-white">
            <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00] mb-2">[ NEW 360° REVIEW ]</div>
            <h2 className="font-display font-bold text-3xl mb-2">Start 360° review</h2>
            <p className="text-sm text-[#666] mb-5">We'll email {profile.employee_name} a secure link to nominate the people who should rate them. You'll approve the final list before anyone is contacted.</p>
            <div className="space-y-4">
              <div>
                <label className="cal-label">Title (optional)</label>
                <input autoFocus value={cycle360Form.title} onChange={(e) => setCycle360Form((f) => ({ ...f, title: e.target.value }))}
                  placeholder={`Q1 2026 360 for ${profile.employee_name}`} className="cal-input" data-testid="start-360-title" />
              </div>
              <div>
                <label className="cal-label">Period covered</label>
                <div className="grid grid-cols-3 gap-2">
                  {[{v:"3m",label:"Past 3 months"},{v:"6m",label:"Past 6 months"},{v:"12m",label:"Past 12 months"}].map((p) => (
                    <button key={p.v} type="button" onClick={() => setCycle360Form((f) => ({ ...f, period: p.v }))}
                      className={`p-3 border border-[#111] text-left transition ${cycle360Form.period === p.v ? "bg-[#FF4F00] text-white" : "bg-white hover:bg-[#F4F4F0]"}`}
                      data-testid={`360-period-${p.v}`}>
                      <div className="font-mono-cal text-[10px] uppercase tracking-widest opacity-70">{p.v.toUpperCase()}</div>
                      <div className="font-display font-bold text-sm">{p.label}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="cal-card p-3 bg-[#FAFAF5]">
                <div className="font-mono-cal text-[9px] uppercase tracking-widest text-[#666] mb-1">[ DEFAULTS ]</div>
                <div className="text-xs text-[#333]">Manager <b>50%</b> · Peers <b>25%</b> · Direct Reports <b>25%</b> · min <b>3</b> peers · max <b>6</b> raters</div>
                <div className="text-xs text-[#666] mt-1">These can be tuned later per-cycle in Phase 2 of the 360 engine.</div>
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-6">
              <button onClick={() => setShow360Modal(false)} disabled={starting360} className="cal-btn cal-btn-ghost" data-testid="start-360-cancel">Cancel</button>
              <button onClick={start360Cycle} disabled={starting360} className="cal-btn" data-testid="start-360-confirm">
                {starting360 ? "Starting…" : "Launch →"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Start calibration modal */}
      {showStart && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="start-modal">
          <div className="cal-card cal-shadow max-w-md w-full p-8 bg-white">
            <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00] mb-2">[ NEW CALIBRATION ]</div>
            <h2 className="font-display font-bold text-3xl mb-5">Start calibration</h2>
            <div className="space-y-4">
              <div>
                <label className="cal-label">Title</label>
                <input autoFocus value={startForm.title} onChange={(e) => setStartForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Q1 2026 Performance Review" className="cal-input" data-testid="start-title" />
              </div>
              <div>
                <label className="cal-label">Period covered</label>
                <div className="grid grid-cols-2 gap-2">
                  {PERIODS.map((p) => (
                    <button key={p.v} type="button" onClick={() => setStartForm((f) => ({ ...f, period: p.v }))}
                      className={`p-3 border border-[#111] text-left transition ${startForm.period === p.v ? "bg-[#FF4F00] text-white" : "bg-white hover:bg-[#F4F4F0]"}`}
                      data-testid={`period-${p.v}`}>
                      <div className="font-mono-cal text-[10px] uppercase tracking-widest opacity-70">{p.v.toUpperCase()}</div>
                      <div className="font-display font-bold">{p.label}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-6">
              <button onClick={() => setShowStart(false)} disabled={starting} className="cal-btn cal-btn-ghost" data-testid="start-cancel">Cancel</button>
              <button onClick={startCalibration} disabled={starting} className="cal-btn" data-testid="start-confirm">
                {starting ? "Starting…" : "Create →"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────
          FLOATING DRAWERS — Tasks + Private Notes (Iter40)
          Both slide in from the right. Backdrop click or Esc closes.
          Tasks drawer owns the full tasks editor (incl. Attach PD block
          when no PD yet). Notes drawer owns the private-manager notes
          textarea with auto-save on blur.
      ───────────────────────────────────────────────────────────────── */}
      {showTasksDrawer && (
        <div className="fixed inset-0 z-40 flex" data-testid="tasks-drawer">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowTasksDrawer(false)}
            data-testid="tasks-drawer-backdrop"
          />
          <div className="relative ml-auto h-full w-full sm:max-w-xl md:max-w-2xl bg-white border-l border-[#111] shadow-2xl flex flex-col animate-[slide-in-right_0.18s_ease-out]">
            <div className="flex items-center justify-between gap-3 p-5 border-b border-[#111] bg-[#FAFAF5] shrink-0">
              <div>
                <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00] flex items-center gap-2">
                  <ListChecks size={12} /> [ TASKS FROM PD ]
                </div>
                <h2 className="font-display font-bold text-2xl mt-1">{profile.employee_name}</h2>
                <div className="font-mono-cal text-[11px] text-[#666] mt-1">
                  {profile.pd_filename ? <>PD · {profile.pd_filename}</> : "No PD attached — tasks not yet generated"}
                </div>
              </div>
              <button
                onClick={() => setShowTasksDrawer(false)}
                className="p-2 border border-[#111] hover:bg-[#111] hover:text-white"
                data-testid="tasks-drawer-close"
                aria-label="Close tasks drawer"
              ><X size={16} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {!profile.pd_filename && (
                <div className="cal-card p-5 mb-4 border-2 border-[#FF4F00] bg-[#FFF5F0]" data-testid="attach-pd-card">
                  <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00] mb-1 flex items-center gap-2">
                    <Paperclip size={12} /> [ NO PD ATTACHED ]
                  </div>
                  <h3 className="font-display font-bold text-lg mb-1">Attach a Position Description</h3>
                  <p className="text-[#666] text-sm mb-4">
                    Upload a PDF/DOCX/DOC or pick a saved PD from your library. We&apos;ll extract the tasks and attach them here.
                  </p>
                  <AttachPDPicker profileId={id} onAttached={load} />
                </div>
              )}
              <div className="space-y-3" data-testid="tasks-list">
                {tasks.length === 0 && (
                  <div className="cal-card p-6 text-center">
                    <div className="font-mono-cal text-xs text-[#666] mb-2">[ NO TASKS YET ]</div>
                    <div className="text-sm text-[#666]">Attach a PD or add a task manually below.</div>
                  </div>
                )}
                {tasks.map((t, i) => (
                  <div key={i} className="cal-card p-4 flex gap-3 items-start" data-testid={`task-row-${i}`}>
                    <div className="font-mono-cal text-xs text-[#FF4F00] w-8 pt-2">{String(i + 1).padStart(2, "0")}</div>
                    <textarea value={t.description} onChange={(e) => updTask(i, e.target.value)} rows={2}
                      className="flex-1 p-3 bg-transparent border border-[#111] focus:ring-2 focus:ring-[#FF4F00] outline-none text-[15px] resize-none"
                      data-testid={`task-input-${i}`} />
                    <button onClick={() => rmTask(i)} className="p-2 border border-[#111] hover:bg-[#111] hover:text-white" data-testid={`remove-task-${i}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 p-4 border-t border-[#111] bg-[#FAFAF5] shrink-0">
              <button onClick={addTask} className="cal-btn cal-btn-ghost" data-testid="add-task"><Plus size={14} /> Add task</button>
              <div className="flex items-center gap-3">
                {dirty && <span className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00]">· Unsaved</span>}
                <button
                  onClick={saveTasks}
                  disabled={saving || !dirty}
                  className="cal-btn disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="save-tasks"
                >
                  <Save size={14} /> {saving ? "Saving…" : "Save changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showNotesDrawer && (
        <div className="fixed inset-0 z-40 flex" data-testid="notes-drawer">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowNotesDrawer(false)}
            data-testid="notes-drawer-backdrop"
          />
          <div className="relative ml-auto h-full w-full sm:max-w-xl md:max-w-2xl bg-white border-l border-[#111] shadow-2xl flex flex-col animate-[slide-in-right_0.18s_ease-out]">
            <div className="flex items-center justify-between gap-3 p-5 border-b border-[#111] bg-[#FAFAF5] shrink-0">
              <div>
                <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00] flex items-center gap-2">
                  <NotebookPen size={12} /> [ PRIVATE · MANAGER NOTES ]
                </div>
                <h2 className="font-display font-bold text-2xl mt-1">Running notes on {profile.employee_name}</h2>
                <p className="text-[#666] text-sm mt-1">Date-stamped entries, newest first. Only you can see this timeline.</p>
              </div>
              <button
                onClick={() => setShowNotesDrawer(false)}
                className="p-2 border border-[#111] hover:bg-[#111] hover:text-white"
                data-testid="notes-drawer-close"
                aria-label="Close notes drawer"
              ><X size={16} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {/* New-note composer — pinned at top of the timeline so the next entry is one tap away */}
              <div className="cal-card p-4" data-testid="new-note-composer">
                <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-2">
                  [ NEW NOTE · {new Date().toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short", year: "numeric" }).toUpperCase()} ]
                </div>
                <textarea
                  value={newNoteText}
                  onChange={(e) => setNewNoteText(e.target.value)}
                  onKeyDown={(e) => {
                    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") { e.preventDefault(); addNote(); }
                  }}
                  rows={3}
                  autoFocus
                  placeholder="Context from today's 1:1, a win, a concern, something to revisit…"
                  className="w-full p-3 border border-[#111] focus:ring-2 focus:ring-[#FF4F00] outline-none resize-y text-[15px] leading-relaxed font-sans"
                  data-testid="new-note-textarea"
                  maxLength={20000}
                />
                <div className="mt-2 flex items-center justify-between gap-2">
                  <span className="font-mono-cal text-[10px] text-[#999]">⌘/Ctrl + Enter to add · {newNoteText.length} / 20000</span>
                  <button
                    onClick={addNote}
                    disabled={notesSaving || !newNoteText.trim()}
                    className="cal-btn disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="add-note-btn"
                  >
                    <Plus size={14} /> {notesSaving && !editingNoteId ? "Adding…" : "Add note"}
                  </button>
                </div>
              </div>

              {/* Timeline */}
              {notes.length === 0 ? (
                <div className="cal-card p-6 text-center" data-testid="notes-empty">
                  <div className="font-mono-cal text-xs text-[#666] mb-2">[ NO NOTES YET ]</div>
                  <div className="text-sm text-[#666]">Jot a first note above to start building a record of this coaching relationship.</div>
                </div>
              ) : (
                <div className="space-y-3" data-testid="notes-timeline">
                  {notes.map((n) => (
                    <NoteCard
                      key={n.id}
                      note={n}
                      editing={editingNoteId === n.id}
                      editingText={editingText}
                      onStartEdit={() => { setEditingNoteId(n.id); setEditingText(n.text); }}
                      onCancelEdit={() => { setEditingNoteId(null); setEditingText(""); }}
                      onChangeEdit={setEditingText}
                      onSaveEdit={() => saveNoteEdit(n.id)}
                      onDelete={() => deleteNote(n.id)}
                      saving={notesSaving}
                    />
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between gap-3 p-4 border-t border-[#111] bg-[#FAFAF5] shrink-0">
              <span className="font-mono-cal text-[10px] text-[#666] uppercase tracking-widest" data-testid="notes-footer-count">
                {notes.length} note{notes.length === 1 ? "" : "s"}
              </span>
              <button
                onClick={() => setShowNotesDrawer(false)}
                className="font-mono-cal text-[10px] uppercase tracking-widest text-[#111] underline"
                data-testid="notes-drawer-done"
              >Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Note timeline card (Iter41) ----------
function NoteCard({ note, editing, editingText, onStartEdit, onCancelEdit, onChangeEdit, onSaveEdit, onDelete, saving }) {
  const created = note.created_at ? new Date(note.created_at) : null;
  const edited = note.updated_at && note.updated_at !== note.created_at ? new Date(note.updated_at) : null;
  const stamp = created ? created.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short", year: "numeric" }).toUpperCase() : "—";
  const timeOfDay = created ? created.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "";
  const isLegacy = note.id === "legacy-blob";

  return (
    <div className="cal-card p-4" data-testid={`note-card-${note.id}`}>
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2 font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00]">
          <span>[ {stamp} · {timeOfDay} ]</span>
          {isLegacy && <span className="text-[#999] normal-case">pre-timeline entry</span>}
          {edited && !isLegacy && (
            <span className="text-[#999] normal-case" title={`Last edited ${edited.toLocaleString()}`}>
              edited {edited.toLocaleDateString()}
            </span>
          )}
        </div>
        {!editing && (
          <div className="flex items-center gap-1">
            <button
              onClick={onStartEdit}
              className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] hover:text-[#111] underline"
              data-testid={`edit-note-${note.id}`}
            >Edit</button>
            <button
              onClick={onDelete}
              className="p-1 border border-transparent hover:border-[#B00020] hover:text-[#B00020] ml-1"
              data-testid={`delete-note-${note.id}`}
              title="Delete note"
            ><Trash2 size={12} /></button>
          </div>
        )}
      </div>
      {editing ? (
        <>
          <textarea
            value={editingText}
            onChange={(e) => onChangeEdit(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") { onCancelEdit(); }
              if ((e.ctrlKey || e.metaKey) && e.key === "Enter") { e.preventDefault(); onSaveEdit(); }
            }}
            autoFocus
            rows={Math.max(3, Math.min(10, (editingText.match(/\n/g) || []).length + 2))}
            className="w-full p-3 border border-[#111] focus:ring-2 focus:ring-[#FF4F00] outline-none resize-y text-[15px] leading-relaxed"
            data-testid={`edit-note-textarea-${note.id}`}
            maxLength={20000}
          />
          <div className="mt-2 flex items-center justify-end gap-2">
            <button
              onClick={onCancelEdit}
              className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] underline"
              data-testid={`edit-cancel-${note.id}`}
            >Cancel</button>
            <button
              onClick={onSaveEdit}
              disabled={saving || !editingText.trim()}
              className="cal-btn disabled:opacity-50"
              data-testid={`edit-save-${note.id}`}
            ><Save size={12} /> Save</button>
          </div>
        </>
      ) : (
        <div className="whitespace-pre-wrap text-[15px] leading-relaxed text-[#111]">{note.text}</div>
      )}
    </div>
  );
}
